import { useEffect, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";

import { useSession } from "@/api/auth";
import { startCognitoLogin, takeJustSignedOutFlag } from "@/api/cognito";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { resolveTheme, useThemeStore } from "@/stores/theme";

/**
 * "Sign in with ATTMOS" entry page.
 *
 * Two modes, switched on a sessionStorage flag the logout mutation
 * sets — same pattern pharmaforge ships (issue #101):
 *
 * 1. **Cold visit** (no flag): mounts, auto-fires ``startCognitoLogin``
 *    in an effect. The user sees the card for ~50 ms then the Hosted
 *    UI takes over. If Cognito still has a valid session cookie this
 *    silent-SSOs straight back into the app — no Hosted UI form, no
 *    extra click.
 *
 * 2. **Just signed out** (flag set by :func:`useLogout`): does NOT
 *    auto-redirect — that would silently re-SSO because Cognito's
 *    session cookie outlives our FlowStack-side logout, and the Sign
 *    out click would look like a no-op. Renders an explicit "you're
 *    signed out" card. The button passes ``prompt=login`` so the
 *    Hosted UI is forced to render its sign-in form regardless of
 *    Cognito session state.
 *
 * The page intentionally has no email/password fields: ATTMOS suite
 * sign-in is the single source of truth, owned by the
 * ``attmos-customers`` Cognito pool. The same credentials work
 * across orbit, pharmaforge, and FlowStack — that's the whole point.
 */
interface LocationState {
  returnTo?: string;
}

export function CognitoSignInPage() {
  const location = useLocation();
  // ``isLoading`` is true while react-query's first session fetch is
  // in flight — without it the auto-redirect effect below fires
  // before useSession resolves, blowing an already-authenticated user
  // through a full Hosted UI round-trip just to silent-SSO back. Per
  // Rupert PR-333 r2 #7.
  const { data: session, isLoading: sessionLoading } = useSession();
  const [error, setError] = useState<string | null>(null);
  const [redirecting, setRedirecting] = useState(false);
  const theme = useThemeStore((s) => s.theme);
  const resolvedTheme = resolveTheme(theme);

  // Read the post-logout flag exactly once at mount. Re-reading on
  // every render would flip back to the auto-redirect path the moment
  // the user clicked Sign in (which clears the flag), and pharmaforge
  // documents the same lesson on its Login.tsx.
  const [justSignedOut] = useState<boolean>(() => takeJustSignedOutFlag());

  // Auto-redirect for cold visits. Skip when the session query is
  // still loading (would race), when a session already exists (the
  // Navigate below handles it), when the user just signed out
  // (explicit card), when we're mid-redirect, or when an error is
  // displayed (avoid a redirect loop).
  useEffect(() => {
    if (sessionLoading || session || justSignedOut || redirecting || error) return;
    setRedirecting(true);
    const returnTo = (location.state as LocationState | undefined)?.returnTo ?? "/projects";
    startCognitoLogin({ returnTo }).catch((err: unknown) => {
      setRedirecting(false);
      setError(
        err instanceof Error
          ? err.message
          : "Could not start ATTMOS sign-in. Try again in a moment."
      );
    });
  }, [sessionLoading, session, justSignedOut, redirecting, error, location.state]);

  if (session) {
    const returnTo = (location.state as LocationState | undefined)?.returnTo ?? "/projects";
    return <Navigate to={returnTo} replace />;
  }

  const onSignIn = async (): Promise<void> => {
    setError(null);
    setRedirecting(true);
    try {
      const returnTo = (location.state as LocationState | undefined)?.returnTo ?? "/projects";
      // ``prompt: "login"`` only on the post-logout button — defeats
      // the silent SSO chain documented above. Cold-visit auto-redirect
      // omits it so a returning user with a valid Cognito session
      // sails through without seeing the Hosted UI form.
      await startCognitoLogin({ returnTo, prompt: justSignedOut ? "login" : undefined });
      // Normal flow: ``startCognitoLogin`` redirects the page; this
      // line never runs. If we ARE still here, the redirect failed.
      setRedirecting(false);
      setError("Could not redirect to ATTMOS sign-in.");
    } catch (err) {
      setRedirecting(false);
      setError(
        err instanceof Error
          ? err.message
          : "Could not start ATTMOS sign-in. Try again in a moment."
      );
    }
  };

  return (
    <div className="flex min-h-screen flex-col items-center justify-center px-4">
      {/* Brand lockup above the card — mirrors the Cognito Hosted UI
          arrangement so the page+Hosted-UI handoff reads as one product. */}
      <img
        src={resolvedTheme === "dark" ? "/flowstack-logo-white.png" : "/flowstack-logo-navy.png"}
        alt="FlowStack"
        className="mb-6 h-12 w-auto"
      />
      <Card elevation="raised" className="w-full max-w-md">
        <CardHeader>
          <CardTitle>{justSignedOut ? "You're signed out" : "Sign in to FlowStack"}</CardTitle>
          <CardDescription>
            {justSignedOut
              ? "Sign back in with your ATTMOS suite credentials — the same login that works for orbit, pharmaforge, and affinity-rxiv."
              : "Use your ATTMOS suite credentials — the same login that works for orbit, pharmaforge, and affinity-rxiv."}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          {error ? (
            <div
              role="alert"
              aria-live="polite"
              className="rounded-md border border-destructive/40 bg-destructive/10 px-3 py-2 text-sm text-destructive"
            >
              {error}
            </div>
          ) : null}
          {/* Cold-visit hint only — post-logout users already know
              they'll bounce through the Hosted UI, and showing them
              "you'll be redirected" reads as boilerplate. */}
          {!justSignedOut ? (
            <p className="text-xs text-muted-foreground">
              You&apos;ll be redirected to the ATTMOS sign-in page. After authenticating you&apos;ll
              land back in FlowStack on the page you were viewing.
            </p>
          ) : null}
        </CardContent>
        <CardFooter>
          <Button
            type="button"
            className="w-full"
            onClick={() => {
              void onSignIn();
            }}
            disabled={redirecting}
          >
            {redirecting ? "Redirecting…" : "Sign in with ATTMOS"}
          </Button>
        </CardFooter>
      </Card>
    </div>
  );
}
